import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { UpdateHistoryResponse } from "@/hooks/usePrivateOfficeUpdateHistory";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import Icon from "@/components/ui/icon";
import { CheckCircle2 } from "lucide-react";
import { PRIVATE_OFFICE_STATUS_LABELS } from "@/@types";
import { translateAmenityToPortuguese } from "@/features/vision-360/constants/space-amenities-translations";
import { handleCategoryName } from "@/features/private-room/list/utils/private-office-mappings";
import { isSanitizationLog } from "@/constants/sanitization";
import { CheckField } from "@/constants/check-fields";
import { getCheckFieldsDiff } from "./update-history-modal.utils";
import { VerifiedFieldCard } from "./verified-field-card";

interface UpdateHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  history: UpdateHistoryResponse | undefined;
  isLoading: boolean;
}

type UpdateHistoryLog = UpdateHistoryResponse["audit_logs"][number];

const CHECK_FIELD_LABELS: Record<CheckField, string> = {
  status: "Status",
  price: "Valor Mensal",
  size: "Tamanho",
  capacity: "Capacidade",
  photos: "Fotos",
};

function getFieldLabel(field: CheckField): string {
  return CHECK_FIELD_LABELS[field];
}

/**
 * Human-readable labels for metadata fields shown in the legacy changes section.
 * Fields not listed here fall back to showing the raw key name.
 */
const METADATA_FIELD_LABELS: Record<string, string> = {
  name: "Nome da sala",
  approvalStatus: "Status de Aprovação",
  monthlyPrice: "Valor Mensal",
  yearlyPrice: "Valor Anual",
  amenities: "Amenidades",
  description: "Descrição",
  notes: "Notas",
  availabilityDate: "Data de Disponibilidade",
  lifecycleState: "Estado do Ciclo de Vida",
  operatingHours: "Horário de Funcionamento",
  timezone: "Fuso Horário",
};

/**
 * Metadata fields that are internal/technical and should NOT be shown as changes.
 * checkFieldsUpdates itself is excluded; its tracked fields (status, monthlyPrice,
 * sizeM2, capacity, photos) are handled by getCheckFieldsDiff.
 */
const EXCLUDED_METADATA_FIELDS = new Set([
  "checkFieldsUpdates",
  "status", // shown via getCheckFieldsDiff
  "monthlyPrice", // shown via getCheckFieldsDiff
  "sizeM2", // shown via getCheckFieldsDiff
  "capacity", // shown via getCheckFieldsDiff
  "photos", // shown via getCheckFieldsDiff
  "autoApproved",
  "partnerId",
  "externalId",
  "source",
  "origin",
  "externalUrl",
  // Root-level technical fields (present when entity is flat, not nested under metadata)
  "_id",
  "id",
  "__v",
  "createdAt",
  "updatedAt",
  "created_at",
  "updated_at",
  "metadata",
  "spaceId",
  "buildingId",
]);

const APPROVAL_STATUS_LABELS: Record<string, string> = {
  pending: "Pendente",
  approved: "Aprovado",
  rejected: "Rejeitado",
};

function getChanges(
  prev: Record<string, unknown> | null | undefined,
  curr: Record<string, unknown>,
) {
  // Entity data may be nested under "metadata" or flat at the root level.
  // Prefer the nested structure when "metadata" exists; otherwise fall back to root.
  const currMeta = (
    curr["metadata"] != null
      ? (curr["metadata"] as Record<string, unknown>)
      : curr
  ) as Record<string, unknown>;
  const prevMeta = (
    prev != null && prev["metadata"] != null
      ? (prev["metadata"] as Record<string, unknown>)
      : (prev ?? {})
  ) as Record<string, unknown>;

  const changes = [];

  // Compare all metadata fields (union of keys from both snapshots)
  const allMetaKeys = new Set([
    ...Object.keys(currMeta),
    ...Object.keys(prevMeta),
  ]);

  for (const key of allMetaKeys) {
    if (EXCLUDED_METADATA_FIELDS.has(key)) continue;

    const currValue = currMeta[key];
    const prevValue = prevMeta[key];

    let hasChanged = false;
    try {
      hasChanged = JSON.stringify(prevValue) !== JSON.stringify(currValue);
    } catch {
      hasChanged = prevValue !== currValue;
    }

    if (!hasChanged) continue;

    const label = METADATA_FIELD_LABELS[key] ?? key;
    changes.push({
      field: key,
      label,
      from: prevValue,
      to: currValue,
      hasChanged: true,
    });
  }

  // Also detect top-level name change (entity.name mirrors metadata.name)
  const currName = curr["name"];
  const prevName = prev?.["name"];
  if (
    !changes.find((c) => c.field === "name") &&
    JSON.stringify(currName) !== JSON.stringify(prevName)
  ) {
    changes.push({
      field: "name",
      label: "Nome da sala",
      from: prevName,
      to: currName,
      hasChanged: true,
    });
  }

  return changes;
}

function getStatusLabel(statusValue: unknown): string {
  return PRIVATE_OFFICE_STATUS_LABELS[
    statusValue as keyof typeof PRIVATE_OFFICE_STATUS_LABELS
  ];
}

function formatPhotoValue(value: unknown): string {
  if (!Array.isArray(value) || value.length === 0) return "vazio";
  const n = value.length;
  return `${n} foto${n !== 1 ? "s" : ""}`;
}

function describePhotoDelta(from: unknown, to: unknown): string {
  const fromCount = Array.isArray(from) ? from.length : 0;
  const toCount = Array.isArray(to) ? to.length : 0;
  if (toCount > fromCount) return `+${toCount - fromCount} adicionada(s)`;
  if (toCount < fromCount) return `-${fromCount - toCount} removida(s)`;
  if (fromCount === 0 && toCount === 0) return "";
  return "fotos substituídas";
}

function formatFieldValue(fieldLabel: string, value: unknown): string {
  if (fieldLabel === "Status") {
    return getStatusLabel(value);
  }

  if (fieldLabel === "Status de Aprovação") {
    const label = APPROVAL_STATUS_LABELS[value as string];
    return label ?? String(value ?? "vazio");
  }

  if (value === null || value === undefined) {
    return "vazio";
  }

  if (fieldLabel === "Fotos") return formatPhotoValue(value);

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "vazio";
    }

    const firstItem = value[0];
    if (typeof firstItem === "object" && firstItem !== null) {
      if (fieldLabel === "Amenidades") {
        const allAmenities: string[] = [];

        value.forEach((categoryItem) => {
          if (typeof categoryItem === "object" && categoryItem !== null) {
            const category = categoryItem as Record<string, unknown>;

            if (Array.isArray(category.amenities)) {
              category.amenities.forEach((amenity: unknown) => {
                if (typeof amenity === "object" && amenity !== null) {
                  const amenityObj = amenity as Record<string, unknown>;
                  const amenityName =
                    amenityObj.name || amenityObj.label || amenityObj.slug;
                  if (amenityName) {
                    allAmenities.push(String(amenityName));
                  }
                }
              });
            }
          }
        });

        if (allAmenities.length > 0) {
          return allAmenities.join(", ");
        }
      }

      return value
        .map((item) => {
          if (typeof item === "object" && item !== null) {
            const obj = item as Record<string, unknown>;
            const itemName =
              (obj.name as string) ||
              (obj.label as string) ||
              (obj.title as string);

            return itemName || JSON.stringify(item);
          }
          return String(item);
        })
        .join(", ");
    }

    if (fieldLabel === "Amenidades") {
      return value
        .map((item) => {
          const str = String(item);
          const categoryTranslation = handleCategoryName(str);
          if (categoryTranslation !== str) {
            return categoryTranslation;
          }
          return translateAmenityToPortuguese(str);
        })
        .join(", ");
    }

    return value.join(", ");
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
}

function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "Data inválida";
    }
    return format(date, "dd 'de' MMMM 'de' yyyy 'às' HH:mm", {
      locale: ptBR,
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Data inválida";
  }
}

function getStableValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(getStableValue);
  }

  if (typeof value === "object" && value !== null) {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = getStableValue((value as Record<string, unknown>)[key]);
        return acc;
      }, {});
  }

  return value;
}

function getLogDisplaySignature(log: UpdateHistoryLog): string {
  const changes = getChanges(log.previous_entity_data, log.current_entity_data);
  const checkFieldDiffs = getCheckFieldsDiff(log);
  const verifiedFields = checkFieldDiffs
    .filter((diff) => !diff.changed)
    .map((diff) => ({
      field: diff.field,
      value: getStableValue(diff.currentValue),
    }))
    .sort((a, b) => a.field.localeCompare(b.field));
  const changedCheckFields = checkFieldDiffs
    .filter((diff) => diff.changed)
    .map((diff) => ({
      field: diff.field,
      from: getStableValue(diff.previousValue),
      to: getStableValue(diff.currentValue),
    }));
  const realFieldChanges = changes
    .filter((change) => change.hasChanged)
    .map((change) => ({
      field: change.field,
      from: getStableValue(change.from),
      to: getStableValue(change.to),
    }));
  const allRealChanges = [...changedCheckFields, ...realFieldChanges].sort(
    (a, b) => a.field.localeCompare(b.field),
  );

  return JSON.stringify({
    createdAt: log.created_at,
    executedBy: log.executed_by_user_id ?? log.executed_by ?? "",
    isSanitization: isSanitizationLog(log),
    verifiedFields,
    allRealChanges,
  });
}

function dedupeVisibleLogs(logs: UpdateHistoryLog[]): UpdateHistoryLog[] {
  const seen = new Set<string>();

  return logs.filter((log) => {
    const signature = getLogDisplaySignature(log);
    if (seen.has(signature)) {
      return false;
    }

    seen.add(signature);
    return true;
  });
}

export const UpdateHistoryModal = ({
  isOpen,
  onClose,
  history,
  isLoading,
}: UpdateHistoryModalProps) => {
  const [showSanitization, setShowSanitization] = useState(false);

  const uniqueLogs = useMemo(
    () => dedupeVisibleLogs(history?.audit_logs ?? []),
    [history?.audit_logs],
  );
  const sanitizationCount = useMemo(
    () => uniqueLogs.filter(isSanitizationLog).length,
    [uniqueLogs],
  );
  const visibleLogs = useMemo(
    () =>
      showSanitization
        ? uniqueLogs
        : uniqueLogs.filter((log) => !isSanitizationLog(log)),
    [uniqueLogs, showSanitization],
  );

  return (
    <TooltipProvider>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Histórico de Atualizações</DialogTitle>
              {(history?.audit_logs?.length ?? 0) > 0 && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>Mostrar atualizações automáticas</span>
                  <Switch
                    checked={showSanitization}
                    onCheckedChange={setShowSanitization}
                    aria-label="Mostrar atualizações automáticas de sanitização"
                  />
                </div>
              )}
            </div>
            {!showSanitization && sanitizationCount > 0 && (
              <p className="text-xs text-gray-400 mt-1">
                {sanitizationCount}{" "}
                {sanitizationCount === 1
                  ? "atualização automática oculta"
                  : "atualizações automáticas ocultas"}
              </p>
            )}
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : !visibleLogs.length ? (
            <div className="flex flex-col items-center justify-center py-16 border rounded-lg bg-white">
              <div className="mb-4">
                <Icon name="Clock" size={48} color="#A1A1AA" />
              </div>
              <div className="text-xl font-semibold text-center mb-2">
                Nenhuma alteração registrada até o momento
              </div>
              <div className="text-gray-500 text-center max-w-md text-sm">
                <p>
                  Assim que houver alguma mudança nas informações da sala, ela
                  aparecerá aqui.
                </p>
                <p className="mt-2">
                  Em alguns casos, mesmo que a sala tenha uma data de
                  atualização recente, o histórico pode não estar disponível
                  porque passou a ser registrado apenas a partir do final de
                  junho de 2025.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {visibleLogs.map((log) => {
                const isSanitization = isSanitizationLog(log);
                const changes = getChanges(
                  log.previous_entity_data,
                  log.current_entity_data,
                );
                const checkFieldDiffs = getCheckFieldsDiff(log);
                const verifiedFields = checkFieldDiffs.filter(
                  (d) => !d.changed,
                );
                const changedCheckFields = checkFieldDiffs.filter(
                  (d) => d.changed,
                );
                const realFieldChanges = changes.filter((c) => c.hasChanged);
                const allRealChanges = [
                  ...changedCheckFields.map((diff) => ({
                    field: diff.field,
                    label: getFieldLabel(diff.field),
                    from: diff.previousValue,
                    to: diff.currentValue,
                  })),
                  ...realFieldChanges,
                ];
                const noVisibleChanges =
                  allRealChanges.length === 0 && verifiedFields.length === 0;

                return (
                  <div
                    key={log.id}
                    className="border rounded-xl p-4 space-y-3 shadow-sm hover:shadow-md transition-shadow duration-200"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span className="text-sm text-gray-500">
                          {formatDate(log.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                        <span className="text-sm text-gray-500">
                          {log.executed_by || "Usuário não identificado"}
                        </span>
                        {isSanitization && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="secondary">Sanitização</Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              Atualização automática do sistema — não conta para
                              o score
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    <div>
                      {/* Priority: sanitization card first to avoid confusion with human verification */}
                      {isSanitization ? (
                        <div className="relative overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-lg" />
                          <div className="relative bg-white/90 backdrop-blur-sm rounded-lg p-5">
                            <div className="flex flex-col items-center text-center space-y-3">
                              <div className="relative">
                                <div className="absolute inset-0 bg-green-400 rounded-full blur-lg opacity-25 animate-pulse motion-reduce:animate-none" />
                                <CheckCircle2 className="relative h-10 w-10 text-gray-400" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-600">
                                  Inventário verificado automaticamente
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : noVisibleChanges ? (
                        <div className="relative overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-lg" />
                          <div className="relative bg-white/90 backdrop-blur-sm rounded-lg p-5">
                            <div className="flex flex-col items-center text-center space-y-3">
                              <div className="relative">
                                <div className="absolute inset-0 bg-green-400 rounded-full blur-lg opacity-25 animate-pulse motion-reduce:animate-none" />
                                <CheckCircle2 className="relative h-10 w-10 text-green-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-700">
                                  Inventário verificado • A sala continua com os
                                  mesmos dados
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {/* checkFieldsUpdates: timestamp changed, value same → "permanece" */}
                          {verifiedFields.map((diff) => (
                            <VerifiedFieldCard
                              key={diff.field}
                              fieldLabel={getFieldLabel(diff.field)}
                              currentValue={formatFieldValue(
                                getFieldLabel(diff.field),
                                diff.currentValue,
                              )}
                            />
                          ))}

                          {/* Real field changes */}
                          {allRealChanges.length > 0 && (
                            <>
                              <h4 className="font-semibold mb-3">
                                Alterações:
                              </h4>
                              <div className="space-y-2">
                                {allRealChanges.map((change) => (
                                  <div key={change.field}>
                                    <div className="py-2 grid grid-cols-3 gap-4">
                                      <div className="text-gray-500 text-sm">
                                        {change.label}
                                      </div>
                                      <div className="text-red-600 text-sm">
                                        De:{" "}
                                        {formatFieldValue(
                                          change.label,
                                          change.from,
                                        ) === "vazio" ? (
                                          <span className="italic">vazio</span>
                                        ) : (
                                          formatFieldValue(
                                            change.label,
                                            change.from,
                                          )
                                        )}
                                      </div>
                                      <div className="text-green-600 text-sm">
                                        Para:{" "}
                                        {formatFieldValue(
                                          change.label,
                                          change.to,
                                        ) === "vazio" ? (
                                          <span className="italic">vazio</span>
                                        ) : (
                                          formatFieldValue(
                                            change.label,
                                            change.to,
                                          )
                                        )}
                                      </div>
                                    </div>
                                    {change.label === "Fotos" &&
                                      describePhotoDelta(
                                        change.from,
                                        change.to,
                                      ) !== "" && (
                                        <div className="grid grid-cols-3 gap-4 pb-1">
                                          <div />
                                          <div className="col-span-2 text-xs text-gray-500 italic">
                                            {describePhotoDelta(
                                              change.from,
                                              change.to,
                                            )}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};
